import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { ImportedResume, MergeSummary } from './resume-merge';

/** What the server read; the builder merges it into what is on screen. */
export interface ResumeImportResult {
  imported: ImportedResume;
  source: string;
}

/** The server's own limits, checked here first so a member hears at once. */
export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const IMPORT_ACCEPT =
  '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// As the JST upload: errors come back to the caller, and no page-wide spinner.
const SILENT = new HttpHeaders({ 'X-Skip-Error-Redirect': 'true', 'X-Silent-Request': 'true' });

/**
 * T.8. Start a resume from one you already have (POST /api/resume/import), and
 * the link between the resume builder and Via.
 *
 * Root-provided because Via's chat lives outside the builder: the builder turns
 * `resumeMode` on when the member presses Talk to Via, the chat stream sends it,
 * and when a Via answer added something to the resume the stream calls
 * `changed` with what Via found, which the builder merges into what is on
 * screen. Nothing is saved server-side, so nothing typed can be overwritten.
 */
@Injectable({ providedIn: 'root' })
export class ResumeImportService {
  private readonly http = inject(HttpClient);

  /** Via was opened from the resume builder, so it also fills the resume in. */
  public readonly resumeMode = signal(false);

  /** A Via answer said something a resume can use. */
  public readonly changed = new Subject<ImportedResume>();

  /**
   * What the resume holds and lacks right now, in plain words, and what Via
   * opened the conversation with. Sent with every message in resume mode so
   * Via asks for what is missing instead of what is already there.
   */
  public readonly context = signal('');

  /**
   * T.10 S12, D68: `{ for: 'wizard' }` asks for the wizard's smaller reply (no
   * bullet lines, summary, city, email, phone, education level or year). With
   * no option, today's builder gets the full reply, unchanged.
   */
  public importFile(file: File, options?: { for?: 'wizard' }): Observable<ResumeImportResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    const url = options?.for === 'wizard' ? 'resume/import?for=wizard' : 'resume/import';
    return this.http.post<ResumeImportResult>(url, form, { headers: SILENT });
  }
}

/** "2 jobs, 3 skills and your contact details", for the line and the announcement. */
export function describeAdded(added: MergeSummary): string {
  const parts: string[] = [];
  const n = (count: number, one: string, many: string) =>
    count ? `${count} ${count === 1 ? one : many}` : '';
  [
    n(added.work, 'job', 'jobs'),
    n(added.edu, 'school', 'schools'),
    n(added.creds, 'certification', 'certifications'),
    n(added.skills, 'skill', 'skills'),
    added.summary ? 'a summary' : '',
    added.contact.length ? 'contact details' : ''
  ]
    .filter(Boolean)
    .forEach((p) => parts.push(p));
  if (parts.length <= 1) {
    return parts[0] ?? '';
  }
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
